import { useState, useEffect } from 'react';
import { format, addDays } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@shared/store/authStore';
import { notifyAdminAboutBooking } from '../utils/bookingNotifications';

interface BeautyService {
  service_id: number;
  parent_id?: number;
  is_group: number;
  service_name: string;
  service_code?: string;
  duration: number;
  service_price: number;
  service_type: number;
}

interface Employee {
  Id: number;
  first_name: string;
  last_name: string;
  middle_name: string;
  spec_name: string;
  department: any;
  services?: Array<{
    service_id: number;
    service_name: string;
  }>;
}

interface TimeSlot {
  time: string;
  available: boolean;
}

export function BeautyServicesPage() {
  const navigate = useNavigate();
  const { user, apiClient } = useAuthStore();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState<BeautyService[]>([]);
  const [serviceCategories, setServiceCategories] = useState<BeautyService[]>([]);
  const [groupedServices, setGroupedServices] = useState<Record<number, BeautyService[]>>({});
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [selectedService, setSelectedService] = useState<BeautyService | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [selectedMaster, setSelectedMaster] = useState<Employee | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Generate available dates (next 14 days)
  const availableDates = Array.from({ length: 14 }, (_, i) => {
    const date = addDays(new Date(), i);
    return {
      value: format(date, 'yyyy-MM-dd'),
      label: format(date, 'dd MMMM, EEEE', { locale: ru }),
      isToday: i === 0,
    };
  });

  useEffect(() => {
    loadBeautyServices();
    loadAllEmployees();
  }, [apiClient]);

  useEffect(() => {
    if (selectedMaster && selectedDate) {
      loadFreeTime();
    }
  }, [selectedMaster, selectedDate]);

  useEffect(() => {
    // When service is selected, filter employees who can provide this service
    if (selectedService && employees.length > 0) {
      const filtered = employees.filter(emp => {
        // Check if employee has services array and if it includes the selected service
        return emp.services?.some(s => s.service_id === selectedService.service_id);
      });
      setFilteredEmployees(filtered);
    }
  }, [selectedService, employees]);

  const loadBeautyServices = async () => {
    if (!apiClient) return;

    setIsLoading(true);
    try {
      const allServices = await apiClient.getBookingServices();

      // Объединяем BEAUTY и SPA в одну страницу
      const BEAUTY_ROOT_ID = 895;
      const SPA_ROOT_ID = 397;
      const ROOT_IDS = [BEAUTY_ROOT_ID, SPA_ROOT_ID];

      // Find the root categories
      const beautyRoot = allServices.find((s: BeautyService) => s.service_id === BEAUTY_ROOT_ID);
      const spaRoot = allServices.find((s: BeautyService) => s.service_id === SPA_ROOT_ID);

      if (!beautyRoot && !spaRoot) {
        console.warn('[Beauty+SPA] No root categories found');
        setServiceCategories([]);
        setServices([]);
        setGroupedServices({});
        return;
      }

      // Get all direct child categories of BEAUTY and SPA
      const categories = allServices.filter((service: BeautyService) => {
        return service.is_group === 1 && ROOT_IDS.includes(service.parent_id || 0);
      });

      console.log('[Beauty+SPA] Found categories:', categories);

      // Get category IDs to find their services
      const categoryIds = categories.map((cat: BeautyService) => cat.service_id);

      // Get all services (is_group: 0) that belong to these categories
      const combinedServices = allServices.filter((service: BeautyService) => {
        if (service.is_group !== 0) return false;

        let currentParentId = service.parent_id;
        const maxDepth = 10;
        let depth = 0;

        while (currentParentId && depth < maxDepth) {
          if (categoryIds.includes(currentParentId)) {
            return true;
          }
          if (ROOT_IDS.includes(currentParentId)) {
            return true;
          }

          const parentService = allServices.find((s: BeautyService) => s.service_id === currentParentId);
          if (!parentService) break;

          currentParentId = parentService.parent_id;
          depth++;
        }

        return false;
      });

      console.log('[Beauty+SPA] Found services:', combinedServices.length);

      // Group services by their TOP-LEVEL category
      const grouped: Record<number, BeautyService[]> = {};
      combinedServices.forEach((service: BeautyService) => {
        let topLevelCategoryId = service.parent_id;
        let currentParentId = service.parent_id;
        const maxDepth = 10;
        let depth = 0;

        while (currentParentId && depth < maxDepth) {
          const parentService = allServices.find((s: BeautyService) => s.service_id === currentParentId);
          // Check if parent is one of the root categories
          if (parentService && ROOT_IDS.includes(parentService.parent_id || 0)) {
            topLevelCategoryId = currentParentId;
            break;
          }

          if (ROOT_IDS.includes(currentParentId)) {
            topLevelCategoryId = service.parent_id;
            break;
          }

          if (!parentService) break;
          currentParentId = parentService.parent_id;
          depth++;
        }

        const groupKey = topLevelCategoryId || 0;
        if (!grouped[groupKey]) {
          grouped[groupKey] = [];
        }
        grouped[groupKey].push(service);
      });

      console.log('[Beauty+SPA] Categories:', categories);
      console.log('[Beauty+SPA] Grouped services:', grouped);

      setServiceCategories(categories);
      setServices(combinedServices);
      setGroupedServices(grouped);
    } catch (error) {
      console.error('[Beauty+SPA] Error loading services:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllEmployees = async () => {
    if (!apiClient) return;

    setIsLoading(true);
    try {
      const allEmployees = await apiClient.getEmployees();

      // Filter employees who have beauty services
      const employeesWithBeautyServices = allEmployees.filter((emp: Employee) => {
        return emp.services && emp.services.length > 0;
      });

      setEmployees(employeesWithBeautyServices);
    } catch (error) {
      console.error('[Beauty] Error loading employees:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFreeTime = async () => {
    if (!selectedMaster || !selectedDate || !apiClient || !selectedService) return;

    setIsLoading(true);
    try {
      // ID кабинетов для массажа и педикюра
      const BEAUTY_ROOM_DEVICES = [18, 22];

      // Загружаем свободное время специалиста
      const empFreeTimeData = await apiClient.getEmpFreeTime(selectedMaster.Id, selectedDate);

      // Загружаем свободное время кабинетов
      const deviceFreeTimeData = await apiClient.getDeviceFreeTime(BEAUTY_ROOM_DEVICES, selectedDate);

      // Объединяем свободное время всех кабинетов в один массив интервалов
      const deviceTimeRanges: Array<{ time_from: string; time_to: string }> = [];

      if (Array.isArray(deviceFreeTimeData) && deviceFreeTimeData.length > 0) {
        deviceFreeTimeData.forEach((deviceData: any) => {
          if (deviceData.free_time && Array.isArray(deviceData.free_time)) {
            deviceData.free_time.forEach((timeRange: any) => {
              if (timeRange.time_from && timeRange.time_to) {
                deviceTimeRanges.push({
                  time_from: timeRange.time_from,
                  time_to: timeRange.time_to
                });
              }
            });
          }
        });
      }

      const slots: TimeSlot[] = [];
      const interval = 30; // Интервал между слотами - 30 минут
      const serviceDuration = selectedService.duration || 60; // Длительность услуги в минутах

      // Get current time for filtering past slots on today
      const now = new Date();
      const isToday = selectedDate === format(now, 'yyyy-MM-dd');
      const currentTotalMinutes = now.getHours() * 60 + now.getMinutes();

      // Генерируем слоты ИЗ СВОБОДНОГО ВРЕМЕНИ СПЕЦИАЛИСТА
      if (Array.isArray(empFreeTimeData) && empFreeTimeData.length > 0) {
        empFreeTimeData.forEach((empData: any) => {
          if (empData.free_time && Array.isArray(empData.free_time) && empData.free_time.length > 0) {
            empData.free_time.forEach((timeRange: any) => {
              if (timeRange.time_from && timeRange.time_to) {
                const [startHour, startMinute] = timeRange.time_from.split(':').map(Number);
                const [endHour, endMinute] = timeRange.time_to.split(':').map(Number);
                const startTotalMinutes = startHour * 60 + startMinute;
                const endTotalMinutes = endHour * 60 + endMinute;

                // Генерируем слоты с интервалом 30 минут
                // НО генерируем только те, которые заканчиваются ДО конца интервала с учетом длительности
                const lastPossibleStart = endTotalMinutes - serviceDuration;

                for (let minutes = startTotalMinutes; minutes <= lastPossibleStart; minutes += interval) {
                  // Пропускаем прошедшее время для сегодняшнего дня
                  if (isToday && minutes <= currentTotalMinutes) continue;

                  // Конец слота = начало + длительность услуги
                  const slotEndMinutes = minutes + serviceDuration;

                  const hour = Math.floor(minutes / 60);
                  const minute = minutes % 60;
                  const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;

                  // Если нет данных о кабинетах - показываем все слоты специалиста
                  if (deviceTimeRanges.length === 0) {
                    slots.push({ time: timeSlot, available: true });
                  } else {
                    // Проверяем: свободен ли ХОТЯ БЫ ОДИН кабинет на ВСЮ длительность услуги?
                    const isAnyDeviceAvailable = deviceTimeRanges.some(deviceRange => {
                      const [devStartHour, devStartMinute] = deviceRange.time_from.split(':').map(Number);
                      const [devEndHour, devEndMinute] = deviceRange.time_to.split(':').map(Number);
                      const devStartMinutes = devStartHour * 60 + devStartMinute;
                      const devEndMinutes = devEndHour * 60 + devEndMinute;

                      // Вся услуга (начало + длительность) должна помещаться в свободное время кабинета
                      return minutes >= devStartMinutes && slotEndMinutes <= devEndMinutes;
                    });

                    // Добавляем слот только если хотя бы один кабинет свободен на всю длительность
                    if (isAnyDeviceAvailable) {
                      slots.push({ time: timeSlot, available: true });
                    }
                  }
                }
              }
            });
          }
        });
      }

      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error loading free time:', error);
      setAvailableSlots([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getServiceIcon = (serviceName: string): string => {
    const name = serviceName.toLowerCase();
    if (name.includes('массаж') || name.includes('кинезия')) return '🧑🏼‍🔧';
    if (name.includes('волос') || name.includes('уход')) return '💆‍♀️';
    if (name.includes('стрижк') || name.includes('мужск')) return '💇';
    if (name.includes('визаж')) return '💄';
    if (name.includes('брови')) return '✨';
    if (name.includes('косметолог')) return '🧴';
    if (name.includes('маникюр')) return '💅';
    if (name.includes('педикюр')) return '🦶';
    return '💅';
  };

  const getServiceCategory = (serviceName: string): string => {
    if (serviceName.toLowerCase().includes('визаж')) return 'ВИЗАЖ';
    if (serviceName.toLowerCase().includes('брови')) return 'БРОВИ';
    if (serviceName.toLowerCase().includes('косметолог')) return 'КОСМЕТОЛОГИЯ';
    if (serviceName.toLowerCase().includes('массаж')) return 'МАССАЖ';
    if (serviceName.toLowerCase().includes('маникюр')) return 'МАНИКЮР';
    if (serviceName.toLowerCase().includes('педикюр')) return 'ПЕДИКЮР';
    return 'BEAUTY';
  };

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  const handleBooking = async () => {
    if (!selectedService || !selectedMaster || !selectedDate || !selectedTime || !user || !apiClient) return;

    try {
      setIsLoading(true);

      const BEAUTY_ROOM_DEVICES = [18, 22];
      const [hours, minutes] = selectedTime.split(':');
      const duration = selectedService.duration || 60; // minutes
      const startMinutes = parseInt(hours) * 60 + parseInt(minutes);
      const endMinutes = startMinutes + duration;
      const endHour = Math.floor(endMinutes / 60);
      const endMinute = endMinutes % 60;

      // Проверяем, что услуга помещается в свободное время специалиста
      const freeTimeSlots = await apiClient.getEmpFreeTime(selectedMaster.Id, selectedDate);
      const empFreeTimeData = Array.isArray(freeTimeSlots) ? freeTimeSlots : (freeTimeSlots?.emp_free_time || []);

      let isValidBooking = false;

      if (Array.isArray(empFreeTimeData) && empFreeTimeData.length > 0) {
        for (const empData of empFreeTimeData) {
          if (empData.free_time && Array.isArray(empData.free_time)) {
            for (const timeRange of empData.free_time) {
              const [rangeStartHour, rangeStartMinute] = timeRange.time_from.split(':').map(Number);
              const [rangeEndHour, rangeEndMinute] = timeRange.time_to.split(':').map(Number);
              const rangeStartMinutes = rangeStartHour * 60 + rangeStartMinute;
              const rangeEndMinutes = rangeEndHour * 60 + rangeEndMinute;

              // Проверяем, что запись начинается и заканчивается внутри диапазона
              if (startMinutes >= rangeStartMinutes && endMinutes <= rangeEndMinutes) {
                isValidBooking = true;
                break;
              }
            }
            if (isValidBooking) break;
          }
        }
      }

      if (!isValidBooking) {
        alert(`Невозможно забронировать услугу "${selectedService.service_name}" (${duration} минут) на время ${selectedTime}.\n\nУслуга выходит за пределы доступного времени.`);
        setIsLoading(false);
        return;
      }

      // Проверяем доступность кабинетов и находим свободный
      const deviceFreeTime = await apiClient.getDeviceFreeTime(BEAUTY_ROOM_DEVICES, selectedDate);
      let availableDeviceId = null;

      if (Array.isArray(deviceFreeTime) && deviceFreeTime.length > 0) {
        for (const device of deviceFreeTime) {
          if (device.free_time && Array.isArray(device.free_time)) {
            for (const timeRange of device.free_time) {
              const [devStartHour, devStartMinute] = timeRange.time_from.split(':').map(Number);
              const [devEndHour, devEndMinute] = timeRange.time_to.split(':').map(Number);
              const devStartMinutes = devStartHour * 60 + devStartMinute;
              const devEndMinutes = devEndHour * 60 + devEndMinute;

              // Проверяем, что вся услуга помещается в свободное время кабинета
              if (startMinutes >= devStartMinutes && endMinutes <= devEndMinutes) {
                availableDeviceId = device.DeviceId;
                break;
              }
            }
            if (availableDeviceId) break;
          }
        }
      }

      // ❌ КРИТИЧЕСКАЯ ПРОВЕРКА: кабинет ОБЯЗАТЕЛЕН для бьюти-услуг
      if (!availableDeviceId) {
        alert(`Невозможно забронировать услугу "${selectedService.service_name}" на время ${selectedTime}.\n\nВсе кабинеты заняты на выбранное время.\n\nПожалуйста, выберите другое время.`);
        setIsLoading(false);
        return;
      }

      const timeFrom = `${selectedDate} ${selectedTime}`;
      const timeTo = `${selectedDate} ${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;

      // Создаем бронирование с DeviceId
      const result = await apiClient.createBooking({
        EmpId: selectedMaster.Id,
        ClientId: user.id,
        date_time_start: timeFrom,
        date_time_end: timeTo,
        note: `${selectedService.service_name}`,
        ServiceId: selectedService.service_id,
        DeviceId: availableDeviceId
      });

      // Отправляем уведомление админу в Telegram
      await notifyAdminAboutBooking({
        clientId: user.id,
        clientName: `${user.firstName} ${user.lastName}`,
        date: format(new Date(selectedDate), 'dd.MM.yyyy', { locale: ru }),
        time: selectedTime,
        serviceName: selectedService.service_name,
        employeeName: `${selectedMaster.first_name} ${selectedMaster.last_name}`,
        bookingId: result?.id || result?.booking_id || 'N/A'
      });

      // Redirect to profile to see the new booking
      navigate('/profile');
    } catch (error) {
      console.error('Booking error:', error);
      alert('Ошибка при бронировании. Попробуйте снова.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: 'var(--bg-secondary)' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => {
              if (step > 1) {
                setStep(step - 1);
                if (step === 2) setSelectedService(null);
                if (step === 3) setSelectedMaster(null);
                if (step === 4) setSelectedDate('');
                if (step === 5) setSelectedTime('');
              } else {
                navigate('/');
              }
            }}
            className="flex items-center mb-4 hover:opacity-75"
            style={{ color: 'var(--text-secondary)' }}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Назад
          </button>

          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Beauty & SPA</h1>
          <p style={{ color: 'var(--text-secondary)' }}>Косметология, массаж и SPA процедуры</p>
        </div>

        {/* Progress */}
        <div className="flex items-center mb-8 overflow-x-auto">
          {[1, 2, 3, 4, 5].map((stepNum) => (
            <div key={stepNum} className="flex items-center flex-shrink-0">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= stepNum ? 'bg-pink-600 text-white' : ''
                }`}
                style={step >= stepNum ? {} : { backgroundColor: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
              >
                {stepNum}
              </div>
              {stepNum < 5 && (
                <div
                  className={`w-12 h-1 ${step > stepNum ? 'bg-pink-600' : ''}`}
                  style={step > stepNum ? {} : { backgroundColor: 'var(--border-color)' }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Step 1: Service Selection */}
        {step === 1 && (
          <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>1. Выберите услугу</h2>
              {isLoading && (
                <div className="w-6 h-6 border-2 border-pink-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>

            {/* Display services grouped by categories - Tree with expandable sections */}
            <div className="space-y-3">
              {serviceCategories.map((category) => {
                const categoryServices = groupedServices[category.service_id] || [];
                if (categoryServices.length === 0) return null;

                const isExpanded = expandedCategories.has(category.service_id);

                return (
                  <div key={category.service_id} className="border rounded-xl overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                    {/* Category Header - Clickable to expand/collapse */}
                    <button
                      onClick={() => toggleCategory(category.service_id)}
                      className="w-full p-4 flex items-center justify-between hover:bg-pink-50 transition-colors"
                      style={{ backgroundColor: isExpanded ? 'rgba(236, 72, 153, 0.05)' : 'var(--card-bg)' }}
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getServiceIcon(category.service_name)}</span>
                        <div className="text-left">
                          <h3 className="text-lg font-bold text-pink-700">{category.service_name}</h3>
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {categoryServices.length} {categoryServices.length === 1 ? 'услуга' : 'услуги'}
                          </p>
                        </div>
                      </div>
                      <svg
                        className={`w-5 h-5 text-pink-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {/* Services in this category - Only show when expanded */}
                    {isExpanded && (
                      <div className="border-t p-4 space-y-3" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
                        {categoryServices.map((service) => (
                          <button
                            key={service.service_id}
                            onClick={() => {
                              setSelectedService(service);
                              setStep(2);
                            }}
                            className={`w-full p-4 border rounded-lg text-left hover:border-pink-500 transition-colors ${
                              selectedService?.service_id === service.service_id ? 'border-pink-500 bg-pink-50' : ''
                            }`}
                            style={selectedService?.service_id === service.service_id ? {} : {
                              borderColor: 'var(--border-color)',
                              backgroundColor: 'var(--card-bg)'
                            }}
                          >
                            <h4 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                              {service.service_name}
                            </h4>

                            <div className="flex items-center justify-between mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                              <div>
                                {service.duration > 0 && (
                                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                    ⏱ {service.duration} мин
                                  </div>
                                )}
                              </div>
                              <div className="text-lg font-bold text-pink-600">
                                {service.service_price > 0 ? `${service.service_price}₽` : 'Уточните цену'}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Services without category (parent_id = 0 or null) */}
              {groupedServices[0] && groupedServices[0].length > 0 && (
                <div className="border rounded-xl overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                  {/* Other services category header */}
                  <button
                    onClick={() => toggleCategory(0)}
                    className="w-full p-4 flex items-center justify-between hover:bg-pink-50 transition-colors"
                    style={{ backgroundColor: expandedCategories.has(0) ? 'rgba(236, 72, 153, 0.05)' : 'var(--card-bg)' }}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">✨</span>
                      <div className="text-left">
                        <h3 className="text-lg font-bold text-pink-700">Другие услуги</h3>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                          {groupedServices[0].length} {groupedServices[0].length === 1 ? 'услуга' : 'услуги'}
                        </p>
                      </div>
                    </div>
                    <svg
                      className={`w-5 h-5 text-pink-600 transition-transform ${expandedCategories.has(0) ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Services list - only show when expanded */}
                  {expandedCategories.has(0) && (
                    <div className="border-t p-4 space-y-3" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--card-bg)' }}>
                      {groupedServices[0].map((service) => (
                        <button
                          key={service.service_id}
                          onClick={() => {
                            setSelectedService(service);
                            setStep(2);
                          }}
                          className={`w-full p-4 border rounded-lg text-left hover:border-pink-500 transition-colors ${
                            selectedService?.service_id === service.service_id ? 'border-pink-500 bg-pink-50' : ''
                          }`}
                          style={selectedService?.service_id === service.service_id ? {} : {
                            borderColor: 'var(--border-color)',
                            backgroundColor: 'var(--card-bg)'
                          }}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="text-xl mb-1">{getServiceIcon(service.service_name)}</div>
                              <h4 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                                {service.service_name}
                              </h4>
                            </div>
                          </div>

                          <div className="flex items-center justify-between mt-2 pt-2 border-t" style={{ borderColor: 'var(--border-color)' }}>
                            <div>
                              {service.duration > 0 && (
                                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                                  ⏱ {service.duration} мин
                                </div>
                              )}
                            </div>
                            <div className="text-lg font-bold text-pink-600">
                              {service.service_price > 0 ? `${service.service_price}₽` : 'Уточните цену'}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {services.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">💆</div>
                <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Услуги не найдены</h3>
                <p style={{ color: 'var(--text-secondary)' }}>В данный момент Beauty & SPA услуги недоступны</p>
              </div>
            )}
          </div>
        )}

        {/* Step 2: Master Selection */}
        {step === 2 && selectedService && (
          <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                  2. Выберите мастера для услуги
                </h2>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                  {selectedService.service_name}
                </p>
              </div>
              {isLoading && (
                <div className="w-6 h-6 border-2 border-pink-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEmployees.map((master) => (
                <button
                  key={master.Id}
                  onClick={() => {
                    setSelectedMaster(master);
                    setStep(3);
                  }}
                  className={`p-6 border rounded-2xl text-left hover:border-pink-500 transition-colors ${
                    selectedMaster?.Id === master.Id ? 'border-pink-500 bg-pink-50' : ''
                  }`}
                  style={selectedMaster?.Id === master.Id ? {} : {
                    borderColor: 'var(--border-color)',
                    backgroundColor: 'var(--card-bg)'
                  }}
                >
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center text-white font-semibold">
                      {master.first_name.charAt(0)}{master.last_name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                        {master.first_name} {master.last_name}
                      </h3>
                      {master.middle_name && master.middle_name !== '!' && (
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{master.middle_name}</p>
                      )}
                    </div>
                  </div>

                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {master.spec_name}
                  </div>
                </button>
              ))}
            </div>

            {filteredEmployees.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <div className="text-4xl mb-4">👤</div>
                <h3 className="text-lg font-medium mb-2" style={{ color: 'var(--text-primary)' }}>Мастера не найдены</h3>
                <p style={{ color: 'var(--text-secondary)' }}>
                  К сожалению, нет доступных мастеров для выбранной услуги
                </p>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Date Selection */}
        {step === 3 && selectedService && selectedMaster && (
          <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
              3. Выберите дату для записи к {selectedMaster.first_name} {selectedMaster.last_name}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {availableDates.map((date) => (
                <button
                  key={date.value}
                  onClick={() => {
                    setSelectedDate(date.value);
                    setStep(4);
                  }}
                  className={`p-4 border rounded-lg text-center hover:border-pink-500 transition-colors ${
                    selectedDate === date.value ? 'border-pink-500 bg-pink-50' : ''
                  }`}
                  style={selectedDate === date.value ? {} : {
                    borderColor: 'var(--border-color)',
                    backgroundColor: 'var(--card-bg)'
                  }}
                >
                  <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{date.label}</div>
                  {date.isToday && <div className="text-sm text-pink-600">Сегодня</div>}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 4: Time Selection */}
        {step === 4 && selectedService && selectedMaster && selectedDate && (
          <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                4. Выберите время на {format(new Date(selectedDate), 'dd MMMM', { locale: ru })}
              </h2>
              {isLoading && (
                <div className="w-6 h-6 border-2 border-pink-600 border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>

            {availableSlots.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {availableSlots.map((slot) => (
                  <button
                    key={slot.time}
                    onClick={() => {
                      setSelectedTime(slot.time);
                      setStep(5);
                    }}
                    disabled={!slot.available}
                    className={`p-3 border rounded-lg text-center transition-colors ${
                      slot.available
                        ? selectedTime === slot.time
                          ? 'border-pink-500 bg-pink-50'
                          : 'hover:border-pink-500'
                        : 'bg-gray-100 cursor-not-allowed'
                    }`}
                    style={!slot.available || selectedTime === slot.time ? {} : {
                      borderColor: 'var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    {slot.time}
                    {!slot.available && <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>Занято</div>}
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p style={{ color: 'var(--text-secondary)' }} className="mb-4">На выбранную дату нет свободного времени</p>
                <button
                  onClick={() => setStep(3)}
                  className="text-pink-600 hover:text-pink-700"
                >
                  Выбрать другую дату
                </button>
              </div>
            )}
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && selectedService && selectedMaster && selectedDate && selectedTime && (
          <div className="rounded-2xl p-6 shadow-sm" style={{ backgroundColor: 'var(--card-bg)' }}>
            <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>5. Подтверждение записи</h2>

            <div className="space-y-4 mb-6">
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Услуга:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{selectedService.service_name}</span>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Мастер:</span>
                <div className="text-right">
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                    {selectedMaster.first_name} {selectedMaster.last_name}
                  </span>
                  <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>{selectedMaster.spec_name}</div>
                </div>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Дата:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>
                  {format(new Date(selectedDate), 'dd MMMM yyyy', { locale: ru })}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Время:</span>
                <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{selectedTime}</span>
              </div>
              {selectedService.duration > 0 && (
                <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--border-color)' }}>
                  <span style={{ color: 'var(--text-secondary)' }}>Длительность:</span>
                  <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{selectedService.duration} минут</span>
                </div>
              )}
              <div className="flex justify-between py-2">
                <span style={{ color: 'var(--text-secondary)' }}>Стоимость:</span>
                <span className="font-bold text-pink-600 text-lg">
                  {selectedService.service_price > 0 ? `${selectedService.service_price}₽` : 'Уточните цену'}
                </span>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setStep(4)}
                className="flex-1 px-6 py-3 border rounded-lg hover:bg-gray-50"
                style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
              >
                Назад
              </button>
              <button
                onClick={handleBooking}
                disabled={isLoading}
                className="flex-1 px-6 py-3 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50"
              >
                {isLoading ? 'Бронирование...' : 'Подтвердить запись'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
